import { useEffect, useMemo, useState, useRef } from 'react';
import PrecipitationCell from '../components/table/PrecipitationCell';
import {
  MRT_Table,
  type MRT_ColumnDef,
  type MRT_Cell,
  useMaterialReactTable,
} from 'material-react-table';
import DraggableContainer from '../components/DraggableContainer';
import BaseHeader from '../components/table/BaseHeader';
import PrecipitationHeader from '../components/table/PrecipitationHeader';

const BASE_URL = 'https://d2d0vwk3ealx06.cloudfront.net/data';
const HWY_COMBINED_FILE = 'hwy_combined.json.gz';

interface WeatherData {
  temperature: number;
  freezing_rain_prob: number;
  dewpoint: number;
  humidity: number;
  wind_speed: number;
  wind_gust: number;
  wind_direction: number;
  precipitation: number;
  snow_level: number;
  snow: number;
  cloud_cover: number;
  elevation: number;
}

interface DataRow {
  forecast_datetime: string;
  [key: string]: WeatherData | string;
}

interface JsonResponse {
  columns: string[];
  data: DataRow[];
}

export const PrecipitationTablePage = () => {
  const [data, setData] = useState<DataRow[]>([]);
  const [columns, setColumns] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${BASE_URL}/${HWY_COMBINED_FILE}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get the response as a blob
        const blob = await response.blob();
        
        // Create a decompression stream
        const ds = new DecompressionStream('gzip');
        
        // Create a stream from the blob and pipe it through the decompression stream
        const decompressedStream = blob.stream().pipeThrough(ds);
        
        // Convert the decompressed stream to text
        const decompressedBlob = await new Response(decompressedStream).blob();
        const text = await decompressedBlob.text();
        
        // Replace NaN with null before parsing JSON
        const cleanedText = text.replace(/:\s*NaN/g, ': null');
        const jsonData = JSON.parse(cleanedText) as JsonResponse;
        
        setColumns(jsonData.columns);
        setData(jsonData.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const tableColumns = useMemo<MRT_ColumnDef<DataRow>[]>(() => {
    const baseColumns: MRT_ColumnDef<DataRow>[] = [
      {
        accessorKey: 'forecast_datetime',
        header: 'Time',
        Header: () => <BaseHeader text="Time" />,
        enableColumnActions: false,
        size: 60,
        minSize: 60,
        maxSize: 60,
        Cell: ({ cell }: { cell: MRT_Cell<DataRow> }) => {
          const date = new Date(cell.getValue<string>());
          const minutes = date.getMinutes();
          const dayOfWeek = date.toLocaleString('en-US', {
            weekday: 'short',
            timeZone: 'America/Los_Angeles'
          });
          const time = date.toLocaleString('en-US', {
            hour: 'numeric',
            ...(minutes > 0 && { minute: '2-digit' }),
            hour12: true,
            timeZone: 'America/Los_Angeles'
          }).replace(' ', '');
          return `${dayOfWeek} ${time}`;
        },
        muiTableHeadCellProps: {
          sx: {
            position: 'sticky',
            left: 0,
            backgroundColor: '#ffffff',
            zIndex: 4,  // Higher than the table head's z-index of 3
            '@media (max-width: 600px)': {
              minWidth: '80px !important',
              maxWidth: '80px !important',
              width: '80px !important',
            },
          }
        },
        muiTableBodyCellProps: {
          sx: {
            position: 'sticky',
            left: 0,
            zIndex: 2,
            backgroundColor: '#ffffff',
            padding: {
              xs: '0px 0px 0px', // smaller padding on mobile, 1px bottom padding
              sm: '1px 0px 0px', // reduced padding for larger screens, 1px bottom padding
            },
            fontSize: '0.8rem',
            userSelect: 'none',
            '@media (max-width: 600px)': {
              minWidth: '80px !important',
              maxWidth: '80px !important',
              width: '80px !important',
            },
          }
        },
      },
    ];

    const precipitationColumns: MRT_ColumnDef<DataRow>[] = columns.map((col) => ({
      id: col,
      header: col,
      Header: () => <PrecipitationHeader text={col} />,
      accessorFn: (row: DataRow) => {
        const locationData = row[col] as WeatherData;
        return locationData?.precipitation ?? null;
      },
      Cell: ({ cell, row }: { cell: MRT_Cell<DataRow>; row: any }) => {
        const value = cell.getValue<number | null>();
        const columnId = cell.column.id;
        const locationData = row.original[columnId] as WeatherData;
        const snow = locationData?.snow ?? 0;
        const freezing_rain_prob = locationData?.freezing_rain_prob ?? 0;
        return <PrecipitationCell value={value} snow={snow} freezing_rain_prob={freezing_rain_prob} />;
      },
      size: 14,
      minSize: 14,
      maxSize: 14,
      align: 'center',
      muiTableHeadCellProps: {
        align: 'center',
        sx: {
          padding: '4px 0px',
          '@media (max-width: 600px)': {
            minWidth: '25px !important',
            maxWidth: '25px !important',
            width: '25px !important',
            fontSize: '0.7rem !important',
            padding: '0px !important',
            textAlign: 'center !important',
            '& > div': {
              textAlign: 'center !important',
              '& > div:last-child': {
                fontSize: '0.7rem !important',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'center !important'
              }
            }
          },
        },
      },
      enableColumnActions: false,
    }));

    return [...baseColumns, ...precipitationColumns];
  }, [columns]);

  const table = useMaterialReactTable({
    columns: tableColumns,
    data,
    enableColumnResizing: false,
    enablePinning: true,
    enableColumnFilters: false,
    enableFilters: false,
    enableColumnActions: false,
    enableSorting: false,
    enableTopToolbar: false,
    enableColumnDragging: false,
    initialState: {
      density: 'compact',
      columnPinning: {
        left: ['forecast_datetime'],
      },
    },
    enablePagination: false,
    muiTableContainerProps: {
      sx: { 
        overflow: 'auto',
        '& .MuiTable-root': {
          borderCollapse: 'separate',
        }
      },
    },
    muiTableHeadProps: {
      sx: {
        position: 'sticky',
        top: 0,
        zIndex: 3,
        '& .MuiTableRow-root': {
          backgroundColor: '#ffffff',
        }
      }
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#ffffff',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        '@media (max-width: 600px)': {
          fontSize: '0.7rem !important',
          padding: '0px !important',
          textAlign: 'center !important',
          '& > div': {
            textAlign: 'center !important',
            '& > div:last-child': {
              fontSize: '0.7rem !important',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'center !important'
            }
          }
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: {
          xs: '0px 0px 1px', // reduced padding on mobile, no bottom padding
          sm: '2px 2px 1px', // reduced padding for larger screens, no bottom padding
        },
        '@media (max-width: 600px)': {
          minWidth: '25px !important',
          maxWidth: '25px !important',
          width: '25px !important',
          padding: '0px !important',
          textAlign: 'center !important',
        },
        fontSize: '0.8rem',
        userSelect: 'none',
      },
    },
  });

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div style={{ height: 'calc(100vh - 35px)', display: 'flex', flexDirection: 'column' }}>
      <h2 style={{ margin: '0px' }}>I-90 Precip Forecast</h2>
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <DraggableContainer containerRef={containerRef}>
          <div style={{ height: 'calc(100vh - 80px)' }}>
            <MRT_Table table={table} />
          </div>
        </DraggableContainer>
      </div>
    </div>
  );
};
